import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';

import { catchError, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { ZraApiError } from '../zra-product-detail/zra-product-detail.service';

export interface ZraTaxCode {
  /**
   * The ZRA tax code
   */
  zraCode: string;

  /**
   * The Wirepick code
   */
  wirepickCode: string;

  /**
   * The description of the tax code
   */
  description: string;

  /**
   * The explanation of the tax code
   */
  explanation: string;

  /**
   * The tax rate
   */
  taxRate: number;

  sortOrder?: number;
  codeName?: string;
  codeDescription?: string;
  remark?: string;
}

export const DEFAULT_ZRA_TAX_CODES: ZraTaxCode[] = [
  {
    zraCode: 'A',
    wirepickCode: 'VAT',
    sortOrder: 1,
    codeName: 'Standard Rated 16%',
    codeDescription: 'Standard Rated 16%',
    description: 'Standard Rated 16%',
    explanation: 'Category applies to products and services which attract VAT at 16% by nature',
    remark: 'Category applies to products and services which attract VAT at 16% by nature',
    taxRate: 16,
  },
  {
    zraCode: 'B',
    wirepickCode: 'VAT',
    sortOrder: 2,
    codeName: 'Minimum Taxable Value',
    codeDescription: 'Minimum Taxable Value',
    description: 'Minimum Taxable Value',
    explanation: 'MTV 16% is charged on the Retail Price recommended by manufacturer of MTV goods.',
    remark: 'MTV 16% is charged on the Retail Price recommended by manufacturer of MTV goods.',
    taxRate: 16,
  },
  {
    zraCode: 'C1',
    wirepickCode: 'VAT',
    sortOrder: 3,
    codeName: 'Exports 0%',
    codeDescription: 'Exports 0%',
    description: 'Exports 0%',
    explanation: 'Applies to exports',
    remark: 'Applies to exports',
    taxRate: 0,
  },
  {
    zraCode: 'C2',
    wirepickCode: 'VAT',
    sortOrder: 4,
    codeName: 'Zero-rating Local Purchases Order transactions 0%',
    codeDescription: 'Zero-rating Local Purchases Order transactions 0%',
    description: 'Zero-rating Local Purchases Order transactions 0%',
    explanation: 'Applies to transactions involving customers or projects granted exemption from paying taxes',
    remark: 'Applies to transactions involving customers or projects granted exemption from paying taxes',
    taxRate: 0,
  },
  {
    zraCode: 'C3',
    wirepickCode: 'VAT',
    sortOrder: 5,
    codeName: 'Zero-rated by nature 0%',
    codeDescription: 'Zero-rated by nature 0%',
    description: 'Zero-rated by nature 0%',
    explanation: 'Category applies to products and services which do not attract VAT at 16% by nature.',
    remark: 'Category applies to products and services which do not attract VAT at 16% by nature.',
    taxRate: 0,
  },
  {
    zraCode: 'D',
    wirepickCode: 'VAT',
    sortOrder: 6,
    codeName: 'Exempt',
    codeDescription: 'Exempt',
    description: 'Exempt',
    explanation: 'The category applies to specific class of products and services exempted from VAT',
    remark: 'The category applies to specific class of products and services exempted from VAT',
    taxRate: 0,
  },
  {
    zraCode: 'E',
    wirepickCode: 'VAT',
    sortOrder: 7,
    codeName: 'Reverse VAT',
    codeDescription: 'Reverse VAT',
    description: 'Reverse VAT',
    explanation: 'Applies to transactions involving imported services.',
    remark: 'Applies to transactions involving imported services. (For both Appointed Tax Agent and Self-invoicing).',
    taxRate: 16,
  },
  {
    zraCode: 'RVAT',
    wirepickCode: 'VAT',
    sortOrder: 8,
    codeName: 'Disbursement',
    codeDescription: 'Disbursement',
    description: 'Disbursement',
    explanation: 'Applies to expenses incurred by suppliers on behalf of customers while supplying a product or service.',
    remark: 'Applies to expenses incurred by suppliers on behalf of customers while supplying a product or service.',
    taxRate: 0,
  },
  {
    zraCode: 'IPL1',
    wirepickCode: 'Insurance Premium Levy',
    sortOrder: 9,
    codeName: 'Insurance Premium Levy',
    codeDescription: 'Insurance Premium Levy',
    description: 'Insurance Premium Levy',
    explanation: 'Applies to all classes of insurance except reinsurance.',
    remark: 'Applies to all classes of insurance except reinsurance.',
    taxRate: 0,
  },
  {
    zraCode: 'IPL2',
    wirepickCode: 'Insurance Premium Levy',
    sortOrder: 10,
    codeName: 'Re-Insurance',
    codeDescription: 'Re-Insurance',
    description: 'Re-Insurance',
    explanation: 'Applies only to re-insurance.',
    remark: 'Applies only to re-insurance.',
    taxRate: 0,
  },
  {
    zraCode: 'TL',
    wirepickCode: 'Tourism Levy',
    sortOrder: 11,
    codeName: 'Tourism Levy',
    codeDescription: 'Tourism Levy',
    description: 'Tourism Levy',
    explanation: 'Applies to accommodation and conference facilities.',
    remark: 'Applies to accommodation and conference facilities.',
    taxRate: 0,
  },
  {
    zraCode: 'ECM',
    wirepickCode: 'Excise',
    sortOrder: 12,
    codeName: 'Excise on Coal',
    codeDescription: 'Excise on Coal',
    description: 'Excise on Coal',
    explanation: 'The tax rate is charged per ton',
    remark: 'The tax rate is charged per ton',
    taxRate: 0,
  },
  {
    zraCode: 'EXEEG',
    wirepickCode: 'Excise',
    sortOrder: 13,
    codeName: 'Excise Electricity',
    codeDescription: 'Excise Electricity',
    description: 'Excise Electricity',
    explanation: 'The tax rate is charged per 100kwh',
    remark: 'The tax rate is charged per 100kwh',
    taxRate: 0,
  },
  {
    zraCode: 'TOT',
    wirepickCode: 'Turnover Tax',
    sortOrder: 14,
    codeName: 'No tax charge',
    codeDescription: 'No tax charge',
    description: 'No tax charge',
    explanation: 'Direct tax - tax amount does not reflect on an invoice.',
    remark: 'Direct tax - tax amount does not reflect on an invoice.',
    taxRate: 0,
  },
  {
    zraCode: 'F',
    wirepickCode: 'VAT',
    sortOrder: 15,
    codeName: 'Service Charge 10%',
    codeDescription: 'Service Charge 10%',
    description: 'Service Charge 10%',
    explanation: 'Applies to services rendered',
    remark: 'Applies to services rendered',
    taxRate: 10,
  },
];

@Injectable({
  providedIn: 'root',
})
export class ZraTaxCodesService {
  private base: string;
  private version: string;
  private taxCodesCache: { [entityId: string]: { data: ZraTaxCode[], timestamp: number } } = {};
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private http: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  getTaxCodes(entityId: string): Observable<ZraTaxCode[]> {
    const cachedData = this.getCachedTaxCodes(entityId);
    if (cachedData) {
      return of(cachedData);
    }

    return this.http
      .get<ZraTaxCode[]>(
        `${this.base}api/v${this.version}/${entityId}/ZraTaxCodes`
      )
      .pipe(
        shareReplay(1),
        map(taxCodes => {
          const resolvedTaxCodes = taxCodes?.length ? taxCodes : DEFAULT_ZRA_TAX_CODES;
          this.cacheTaxCodes(entityId, resolvedTaxCodes);
          return resolvedTaxCodes;
        }),
        catchError((_error: HttpErrorResponse) => of(DEFAULT_ZRA_TAX_CODES))
      );
  }

  private getCachedTaxCodes(entityId: string): ZraTaxCode[] | null {
    const cachedItem = this.taxCodesCache[entityId];
    if (cachedItem && (Date.now() - cachedItem.timestamp) < this.CACHE_DURATION) {
      return cachedItem.data;
    }
    return null;
  }

  private cacheTaxCodes(entityId: string, taxCodes: ZraTaxCode[]): void {
    this.taxCodesCache[entityId] = {
      data: taxCodes,
      timestamp: Date.now()
    };
  }

  private handleError(
    error: HttpErrorResponse,
    defaultMessage: string
  ): Observable<never> {
    let errorMessage = defaultMessage;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      try {
        const apiError = error.error as ZraApiError;
        errorMessage =
          apiError.message ||
          `Server error: ${error.status} - ${error.statusText}`;
        if (apiError.details) {
          errorMessage += ` (${apiError.details})`;
        }
      } catch {
        errorMessage = `Server error: ${error.status} - ${
          error.statusText || defaultMessage
        }`;
      }
    }

    return throwError(() => ({
      message: errorMessage,
      status: error.status,
      originalError: error,
    }));
  }
}
